import { GENDER_IDENTITY } from '../../models/registration/Registration'
import { TriageSearch } from '../../models/searchForCare/SearchForCare'
import { TriageScreenerNames } from '../../models/triageScreener/TriageScreener'
import { ManagerCoachingSeatEligibility } from '../managerCoaching/seatEligibility'
import { canPromoteManagerHubBasedOnSeatEligibility } from '../managerHub/utils'
import { PrimaryNeedsWords, TriageScreenerField, TriageWords } from '../searchForCare/constants'
import { isChildSearch } from '../searchForCare/utils'

const getWordCloudFromSearch = (
  search: TriageSearch,
  wordCloudKey: string,
  experienceKey: TriageScreenerField,
): string[] => {
  const questionnaires = search?.body?.questionnaires
  if (questionnaires) {
    const wordCloudData = questionnaires[wordCloudKey] as Dict
    if (wordCloudData) {
      return (wordCloudData[experienceKey] as string[]) ?? []
    }
  }
  return []
}

const checkForWordInWordClouds = (wordsList: TriageWords[], search: TriageSearch) => {
  const wordCloudWords = [
    ...getWordCloudFromSearch(search, TriageScreenerNames.WORD_CLOUD_1, TriageScreenerField.WHAT_ARE_YOU_EXPERIENCING),
    ...getWordCloudFromSearch(
      search,
      TriageScreenerNames.WORD_CLOUD_2,
      TriageScreenerField.WHAT_ARE_YOU_EXPERIENCING_2,
    ),
  ]
  return wordsList.some((word) => wordCloudWords.includes(word))
}

/**
 * See triggers: https://lyrahealth.atlassian.net/wiki/spaces/EN/pages/3448995850/Topical+Toolkits#Toolkit-Ranking%2C-Triggers%2C-Etc
 */
export const checkTriggerConditionsParent = ({ triageSearches }: { triageSearches: TriageSearch[] }): boolean => {
  for (const search of triageSearches) {
    const childSearch = isChildSearch(search)
    const words = [TriageWords.PARENTING_ISSUES, TriageWords.PARENTING]
    const foundTriageParenting = checkForWordInWordClouds(words, search)

    if (foundTriageParenting || childSearch) {
      return true
    }
  }

  return false
}

/**
 * See triggers: https://lyrahealth.atlassian.net/wiki/spaces/EN/pages/3448995850/Topical+Toolkits#Toolkit-Ranking%2C-Triggers%2C-Etc
 */
export const checkTriggerConditionsFamily = ({ triageSearches }: { triageSearches: TriageSearch[] }): boolean => {
  for (const search of triageSearches) {
    const questionnaires = search?.body?.questionnaires
    const childSearch = isChildSearch(search)

    let foundFamilyIndicatorPrimaryNeeds = false
    if (questionnaires) {
      const primaryNeeds = questionnaires[TriageScreenerNames.PRIMARY_NEEDS] as Dict
      if (primaryNeeds) {
        foundFamilyIndicatorPrimaryNeeds =
          primaryNeeds[TriageScreenerField.WHATS_ON_YOUR_MIND] === PrimaryNeedsWords.RELATIONSHIPS_AND_FAMILY
      }
    }

    const words = [
      TriageWords.PREGNANCY_NEW_CHILD,
      TriageWords.SERVING_AS_CAREGIVER,
      TriageWords.PARENTING_ISSUES,
      TriageWords.PARENTING,
    ]
    const foundFamilyIndicatorWordClouds = checkForWordInWordClouds(words, search)

    if (foundFamilyIndicatorPrimaryNeeds || foundFamilyIndicatorWordClouds || childSearch) {
      return true
    }
  }

  return false
}

/**
 * See triggers: https://lyrahealth.atlassian.net/wiki/spaces/EN/pages/3448995850/Topical+Toolkits#Toolkit-Ranking%2C-Triggers%2C-Etc
 */
export const checkTriggerConditionsNeurodiversity = ({
  triageSearches,
}: {
  triageSearches: TriageSearch[]
}): boolean => {
  for (const search of triageSearches) {
    const questionnaires = search?.body?.questionnaires
    const childSearch = isChildSearch(search)
    if (childSearch && questionnaires) {
      const primaryNeedTriggers = [PrimaryNeedsWords.AUTISM_SPECTRUM, PrimaryNeedsWords.DEVELOPMENTAL_CONCERNS]
      const primaryNeeds = questionnaires[TriageScreenerNames.PRIMARY_NEEDS] as Dict
      if (primaryNeeds) {
        const whatsOnYourMind = primaryNeeds[TriageScreenerField.WHATS_ON_YOUR_MIND] as PrimaryNeedsWords | undefined
        if (whatsOnYourMind && primaryNeedTriggers.includes(whatsOnYourMind)) {
          return true
        }
      }
    }
  }

  return false
}

/**
 * See triggers: https://lyrahealth.atlassian.net/wiki/spaces/EN/pages/3448995850/Topical+Toolkits#Toolkit-Ranking%2C-Triggers%2C-Etc
 */
export const checkTriggerConditionsHealthChallenges = ({
  triageSearches,
}: {
  triageSearches: TriageSearch[]
}): boolean => {
  for (const search of triageSearches) {
    const words = [
      TriageWords.CHRONIC_PAIN,
      TriageWords.MAJOR_HEALTH_CONDITIONS,
      TriageWords.BODY_IMAGE,
      TriageWords.BODY_IMAGE_ISSUES,
      TriageWords.SLEEP_ISSUES,
    ]
    const foundHealthChallengesIndicatorWordClouds = checkForWordInWordClouds(words, search)

    if (foundHealthChallengesIndicatorWordClouds) {
      return true
    }
  }

  return false
}

/**
 * See triggers: https://lyrahealth.atlassian.net/wiki/spaces/EN/pages/3448995850/Topical+Toolkits#Toolkit-Ranking%2C-Triggers%2C-Etc
 */
export const checkTriggerConditionsWomensHealth = ({
  triageSearches,
  userGender,
}: {
  triageSearches: TriageSearch[]
  userGender?: string
}): boolean => {
  if (userGender === GENDER_IDENTITY.FEMALE) {
    return true
  }
  for (const search of triageSearches) {
    const words = [TriageWords.PREGNANCY_NEW_CHILD, TriageWords.FERTILITY, TriageWords.MENOPAUSE]
    const foundWomensHealthIndicatorWordClouds = checkForWordInWordClouds(words, search)

    if (foundWomensHealthIndicatorWordClouds) {
      return true
    }
  }

  return false
}

/**
 * See triggers: https://lyrahealth.atlassian.net/wiki/spaces/EN/pages/3448995850/Topical+Toolkits#Toolkit-Ranking%2C-Triggers%2C-Etc
 */
export const checkTriggerConditionsTrauma = ({ triageSearches }: { triageSearches: TriageSearch[] }): boolean => {
  for (const search of triageSearches) {
    const words = [TriageWords.TRAUMATIC_EXPERIENCES]
    const foundTraumaIndicatorWordClouds = checkForWordInWordClouds(words, search)

    if (foundTraumaIndicatorWordClouds) {
      return true
    }
  }

  return false
}

/**
 * Manager Hub triggering comes from the persisted manager signal plus seat eligibility rather
 * than triage searches. Eligible seats with remaining or unknown sessions keep the default
 * rank; exhausted seats may promote. An unknown seat state never promotes.
 */
export const checkTriggerConditionsManagerHub = ({
  isManagerHubTriggered,
  managerCoachingSeatEligibility,
}: {
  isManagerHubTriggered?: boolean
  managerCoachingSeatEligibility?: ManagerCoachingSeatEligibility
}): boolean => !!isManagerHubTriggered && canPromoteManagerHubBasedOnSeatEligibility(managerCoachingSeatEligibility)
